/**
 * Thin fetch wrapper. The browser holds a Cloudflare Access cookie set
 * at the edge, so we just send credentials with every call.
 *
 * `API_BASE` is empty in production — Almanac serves its API same-origin
 * at `almanac.cuprins.com/api/*` via a Workers Route, so relative URLs
 * are correct. The `VITE_API_BASE_URL` fallback is kept as a local-only
 * escape hatch in case you want to point a dev build at a different
 * Worker; in standard local dev it's also unset and Vite proxies `/api`
 * to `localhost:8787`.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

import { inferMimeType } from './files.js';
import { recoverSessionOnce } from './sessionRecovery.js';
import type {
  PackingSuggestInput,
  TripCreateInput,
  TripDTO,
  TripUpdateInput,
  BudgetMonthDTO,
  BudgetSnapshot,
  CurrentUserDTO,
  DocumentCreateInput,
  DocumentDTO,
  DocumentExtraction,
  DocumentUpdateInput,
  MeUpdateInput,
  LifeAreaDTO,
  ObligationCreateInput,
  ObligationDTO,
  ObligationUpdateInput,
  PersonCreateInput,
  PersonDTO,
  PersonUpdateInput,
  PushSubscriptionDTO,
} from '@almanac/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
  // 0 = Cloudflare Access redirected the request (cookie missing/
  // expired); 401 = the Worker received a request but the JWT failed
  // verification. Either way the recovery is the same: reload the
  // page so the navigation hits Access and gets a fresh cookie.
  get isAuth(): boolean {
    return this.status === 0 || this.status === 401;
  }
}

/**
 * Distinct from ApiError because the recovery is different: a network
 * outage just needs to be retried when the connection comes back, but
 * showing "Sign in again" would send the user into a reload that also
 * can't reach the network and looks broken.
 */
export class NetworkError extends Error {
  override readonly cause: unknown;
  constructor(cause: unknown) {
    super('network_error');
    this.cause = cause;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      // When the Access cookie is gone, the edge answers with a 302 to
      // the login flow. Without `manual`, fetch follows the redirect
      // cross-origin into the Cloudflare Access team domain, which CORS
      // turns into a generic network error and we can't tell auth
      // failures from real outages. With `manual`, the redirect surfaces
      // as `res.type === 'opaqueredirect'` and we can react to it.
      redirect: 'manual',
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
  } catch (err) {
    throw new NetworkError(err);
  }

  if (res.type === 'opaqueredirect') {
    // Expired Access session. A reload re-runs the handshake and
    // re-auths silently in the common case; recoverSessionOnce() is
    // guarded so a storm of failing queries triggers one reload, not
    // many. The throw still happens so in-flight queries settle into
    // their error state rather than hanging.
    recoverSessionOnce();
    throw new ApiError(0, 'access_redirect');
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, `HTTP ${res.status}`, body);
  }
  // 204 No Content
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // The signed-in user, for the Settings identity line and per-user
  // preferences (home layout). Resolves the Access JWT to a `users`
  // row server-side.
  me: () => request<{ user: CurrentUserDTO }>('/api/shared/me').then((r) => r.user),

  // Update the caller's own preferences (e.g. home layout). Stored on
  // the user row so the choice follows them across devices.
  updateMe: (input: MeUpdateInput) =>
    request<{ user: CurrentUserDTO }>('/api/shared/me', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }).then((r) => r.user),

  listLifeAreas: () =>
    request<{ lifeAreas: LifeAreaDTO[] }>('/api/almanac/life-areas').then((r) => r.lifeAreas),

  listObligations: () =>
    request<{ obligations: ObligationDTO[] }>('/api/almanac/obligations').then(
      (r) => r.obligations,
    ),

  createObligation: (input: ObligationCreateInput & { id: string }) =>
    request<{ obligation: ObligationDTO }>('/api/almanac/obligations', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.obligation),

  updateObligation: (id: string, input: ObligationUpdateInput) =>
    request<{ obligation: ObligationDTO }>(`/api/almanac/obligations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }).then((r) => r.obligation),

  completeObligation: (id: string, completedAt?: string) =>
    request<{ obligation: ObligationDTO }>(`/api/almanac/obligations/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify(completedAt ? { completedAt } : {}),
    }).then((r) => r.obligation),

  reopenObligation: (id: string, nextDueDate?: string) =>
    request<{ obligation: ObligationDTO }>(`/api/almanac/obligations/${id}/reopen`, {
      method: 'POST',
      body: JSON.stringify(nextDueDate ? { nextDueDate } : {}),
    }).then((r) => r.obligation),

  deleteObligation: (id: string) =>
    request<{ ok: true }>(`/api/almanac/obligations/${id}`, { method: 'DELETE' }),

  listDocuments: () =>
    request<{ documents: DocumentDTO[] }>('/api/almanac/documents').then((r) => r.documents),

  createDocument: (input: DocumentCreateInput & { id: string }) =>
    request<{ document: DocumentDTO }>('/api/almanac/documents', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.document),

  updateDocument: (id: string, input: DocumentUpdateInput) =>
    request<{ document: DocumentDTO }>(`/api/almanac/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }).then((r) => r.document),

  deleteDocument: (id: string) =>
    request<{ ok: true }>(`/api/almanac/documents/${id}`, { method: 'DELETE' }),

  // Restore a just-deleted document — powers the "Undo" toast. Returns
  // the restored document.
  restoreDocument: (id: string) =>
    request<{ document: DocumentDTO }>(`/api/almanac/documents/${id}/restore`, {
      method: 'POST',
    }).then((r) => r.document),


  // Upload (or replace) a document's file. Sends the raw bytes with the
  // filename in a header so the body stays a clean binary stream — the
  // shared `request` helper always sets JSON, so this calls fetch
  // directly. Returns the updated document (now carrying file metadata).
  uploadDocumentFile: async (id: string, file: File): Promise<DocumentDTO> => {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/api/almanac/documents/${id}/file`, {
        method: 'POST',
        credentials: 'include',
        redirect: 'manual',
        headers: {
          // iOS hands back empty `type` for HEIC/some PDFs — infer from
          // the extension so the server accepts it.
          'content-type': inferMimeType(file),
          'x-file-name': encodeURIComponent(file.name),
        },
        body: file,
      });
    } catch (err) {
      throw new NetworkError(err);
    }
    if (res.type === 'opaqueredirect') throw new ApiError(0, 'access_redirect');
    if (!res.ok) {
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = null;
      }
      throw new ApiError(res.status, `HTTP ${res.status}`, body);
    }
    return ((await res.json()) as { document: DocumentDTO }).document;
  },

  // Remove (and permanently delete) a document's attached file, keeping
  // the record. Returns the updated document, now carrying no file.
  removeDocumentFile: (id: string) =>
    request<{ document: DocumentDTO }>(`/api/almanac/documents/${id}/file`, {
      method: 'DELETE',
    }).then((r) => r.document),

  // Same-origin URL the browser can open directly (carries the Access
  // cookie on navigation). Used by the detail card's "Open" action.
  documentFileUrl: (id: string): string => `${API_BASE}/api/almanac/documents/${id}/file`,

  // Read a file with Claude and get back *suggested* metadata (Phase 3).
  // Stateless — nothing is created; the bytes go up exactly like the
  // upload call. `configured: false` means no key is set server-side.
  extractDocument: async (file: File): Promise<DocumentExtraction> => {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/api/almanac/documents/extract`, {
        method: 'POST',
        credentials: 'include',
        redirect: 'manual',
        headers: {
          'content-type': inferMimeType(file),
          'x-file-name': encodeURIComponent(file.name),
        },
        body: file,
      });
    } catch (err) {
      throw new NetworkError(err);
    }
    if (res.type === 'opaqueredirect') throw new ApiError(0, 'access_redirect');
    if (!res.ok) {
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = null;
      }
      throw new ApiError(res.status, `HTTP ${res.status}`, body);
    }
    return (await res.json()) as DocumentExtraction;
  },

  // Budget: every saved month for the calling user (strictly personal
  // server-side — no one else's figures can come back).
  listBudgetMonths: () =>
    request<{ months: BudgetMonthDTO[] }>('/api/almanac/budget/months').then((r) => r.months),

  // Whole-month upsert: the snapshot is small and edits are one-user,
  // so replacing the month atomically beats field-level patching.
  saveBudgetMonth: (month: string, data: BudgetSnapshot) =>
    request<{ month: BudgetMonthDTO }>(`/api/almanac/budget/months/${month}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }).then((r) => r.month),

  // Travel: trips as containers (owner-or-shared server-side).
  listTrips: () => request<{ trips: TripDTO[] }>('/api/almanac/trips').then((r) => r.trips),

  createTrip: (input: TripCreateInput & { id?: string }) =>
    request<{ trip: TripDTO }>('/api/almanac/trips', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.trip),

  updateTrip: (id: string, input: TripUpdateInput) =>
    request<{ trip: TripDTO }>(`/api/almanac/trips/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }).then((r) => r.trip),

  deleteTrip: (id: string) =>
    request<{ ok: boolean }>(`/api/almanac/trips/${id}`, { method: 'DELETE' }),

  // Claude-drafted packing checklist — suggestions only, nothing stored.
  suggestPacking: (input: PackingSuggestInput) =>
    request<{ suggestions: string[] }>('/api/almanac/trips/suggest-packing', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.suggestions),

  listPeople: () =>
    request<{ people: PersonDTO[] }>('/api/shared/people').then((r) => r.people),

  createPerson: (input: PersonCreateInput & { id: string }) =>
    request<{ person: PersonDTO }>('/api/shared/people', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.person),

  updatePerson: (id: string, input: PersonUpdateInput) =>
    request<{ person: PersonDTO }>(`/api/shared/people/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }).then((r) => r.person),

  deletePerson: (id: string) =>
    request<{ ok: true }>(`/api/shared/people/${id}`, { method: 'DELETE' }),

  // Person photo. Raw bytes body like the document upload; the client
  // has already downscaled/cropped to a small square jpeg.
  uploadPersonAvatar: async (id: string, blob: Blob): Promise<PersonDTO> => {
    const res = await fetch(`${API_BASE}/api/shared/people/${id}/avatar`, {
      method: 'POST',
      credentials: 'include',
      redirect: 'manual',
      headers: { 'content-type': blob.type || 'image/jpeg' },
      body: blob,
    });
    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        body = null;
      }
      throw new ApiError(res.status, `HTTP ${res.status}`, body);
    }
    return ((await res.json()) as { person: PersonDTO }).person;
  },

  removePersonAvatar: (id: string) =>
    request<{ person: PersonDTO }>(`/api/shared/people/${id}/avatar`, {
      method: 'DELETE',
    }).then((r) => r.person),

  // Same-origin <img> URL (carries the Access cookie). `v` busts the
  // browser cache when the photo is replaced.
  personAvatarUrl: (id: string, version: string): string =>
    `${API_BASE}/api/shared/people/${id}/avatar?v=${encodeURIComponent(version)}`,

  // Push subscriptions. The browser hands back a `PushSubscription`
  // object; `.toJSON()` flattens it into the shape the API expects
  // — `endpoint`, `keys.{p256dh, auth}`. `deviceId` is a stable
  // per-browser UUID the client keeps in localStorage; the server
  // uses it to upsert the row in place when the endpoint rotates.
  subscribePush: (
    subscription: PushSubscription,
    userAgent?: string,
    timezone?: string,
    deviceId?: string,
  ) =>
    request<{ subscription: { id: string; endpoint: string } }>(
      '/api/shared/notifications/subscriptions',
      {
        method: 'POST',
        body: JSON.stringify({
          ...(subscription.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } }),
          ...(userAgent ? { userAgent } : {}),
          ...(timezone ? { timezone } : {}),
          ...(deviceId ? { deviceId } : {}),
        }),
      },
    ).then((r) => r.subscription),

  unsubscribePush: (endpoint: string) =>
    request<null>('/api/shared/notifications/subscriptions', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),

  // List push subscriptions registered for the calling user. The
  // Settings sheet uses the per-subscription health fields
  // (`pushesSinceAck`, `lastAckedAt`) to spot a silently-broken
  // subscription and prompt the user to reinstall via Safari.
  listSubscriptions: () =>
    request<{ subscriptions: PushSubscriptionDTO[] }>(
      '/api/shared/notifications/subscriptions',
    ).then((r) => r.subscriptions),

  // Wipe every subscription this user has on the server. Paired
  // with the device-side `pushManager.getSubscription().unsubscribe()`
  // and a fresh re-subscribe, this is the escape hatch when the
  // server holds stale endpoints from earlier reinstalls and we
  // need a single, known-good subscription registered.
  resetPushSubscriptions: () =>
    request<{ deleted: number }>('/api/shared/notifications/subscriptions/all', {
      method: 'DELETE',
    }),

  // Synchronous test push. Returns per-subscription results so the
  // caller can show "1 of 2 sent" / specific failure status codes.
  // Useful for diagnosing the push pipeline without waiting for a
  // real obligation to cross its time.
  testPush: () =>
    request<{
      ok: boolean;
      reason?: string;
      results: Array<{
        subscriptionId: string;
        endpoint: string;
        ok: boolean;
        status: number;
        gone: boolean;
        reason: string;
      }>;
    }>('/api/shared/notifications/test', { method: 'POST' }),
};
